import { useContext, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { MultiBackend, TouchTransition } from 'react-dnd-multi-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Form, Formik } from 'formik';

import { Input, SingleSelect } from '@/common';
import { ErrorPage } from '@/components/common/ErrorPage';
import { Breadcrumb, Button, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { PAGE_SIZE } from '@/constants';
import { AuthContext } from '@/contexts';
import { PERMISSION_TYPE } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { getPermissionsForOrganization } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { CeremonyTemplateIcon } from '@/resources';
import {
  useCreateCeremonyTemplateMutation,
  useGetAllOrganizations,
  useGetCeremonyTemplateByIdQuery,
  useGetOrganizationByIdSimplifiedQuery,
  useUpdateCeremonyTemplateMutation
} from '@/services';
import {
  CeremonyTemplateSlot,
  CreateEditCeremonyTemplatePayload,
  Organization,
  SelectOptionProps
} from '@/types';
import { CeremonySlotType } from '@/types/CeremonyTemplateSlotTypes';

import { AddEventTemplateSlot } from './AddEventTemplateSlot';
import { EventTemplateSlotCard } from './EventTemplateSlotCard';
import { createCeremonyTemplateScheme, updateCeremonyTemplateScheme } from './Validation';

export const CreateEditEventTemplate = () => {
  const { eventTemplateId: id } = useParams();
  const [t] = useTranslation('common');
  const isEditPage = !!id;
  const [error, setError] = useState<number>();
  const label = isEditPage ? t('editCeremonyTemplate') : t('addCeremonyTemplate');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isReorderingEnabled, setIsReorderingEnabled] = useState<boolean>(false);
  const [ceremonySlots, setCeremonySlots] = useState<CeremonyTemplateSlot[] | null>([]);
  const {
    currentUserData: currentUser,
    permissions: userPermissions,
    organizationPermissions
  } = useContext(AuthContext);
  const [searchOrganization, setSearchOrganization] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number>(0);

  const hasPermissionViewOrganizations = userPermissions.includes(
    PERMISSION_TYPE.VIEW_ORGANIZATIONS
  );
  const { data: ceremonyTemplate, isLoading: isLoadingCeremonyTemplate } =
    useGetCeremonyTemplateByIdQuery(Number(id), {
      onSuccess: () => undefined,
      onError: (error) => {
        setError(error.status);
      }
    });

  const getInitialValues = () => {
    let organizationId = ceremonyTemplate?.data.organizationId || 0;
    let organizationName = ceremonyTemplate?.data.organization?.name || '';

    if (!isEditPage && organizationId === 0 && currentUser?.organizations?.length === 1) {
      organizationId = currentUser.organizations[0].id;
      organizationName = currentUser.organizations[0].name;
    }

    const initialValues: CreateEditCeremonyTemplatePayload = {
      id: ceremonyTemplate?.data.id,
      name: ceremonyTemplate?.data.name || '',
      organizationId,
      organizationName,
      slots: ceremonyTemplate?.data.slots || []
    };
    return initialValues;
  };

  useEffect(() => {
    setSelectedOrganizationId(getInitialValues().organizationId);
  }, [ceremonyTemplate, currentUser]);

  const { data: selectedOrganization, isFetching: isFetchingSelectedOrganization } =
    useGetOrganizationByIdSimplifiedQuery(selectedOrganizationId);
  const hasCeremonySetupAccess =
    !isFetchingSelectedOrganization &&
    getPermissionsForOrganization(organizationPermissions, selectedOrganization?.data).includes(
      ORGANIZATION_PERMISSION_TYPE.CEREMONY_SETUP
    );
  const ceremonySetupMissingMessage =
    selectedOrganizationId !== 0 && !isFetchingSelectedOrganization && !hasCeremonySetupAccess
      ? t('yourOranizationDoesntHavePermission', {
          permission: t(ORGANIZATION_PERMISSION_TYPE.CEREMONY_SETUP)
        })
      : undefined;

  useEffect(() => {
    if (ceremonyTemplate?.data.slots) {
      const sortedSlots = [...ceremonyTemplate.data.slots].sort((a, b) => a.order - b.order);
      setCeremonySlots(sortedSlots);
    }
  }, [ceremonyTemplate?.data.slots]);

  const handleSubmit = (values: CreateEditCeremonyTemplatePayload) => {
    if (!hasCeremonySetupAccess) {
      return;
    }
    setIsSubmitting(true);
    const payloadCeremonyTemplate = {
      id: values.id,
      name: values.name,
      organizationId: values.organizationId,
      slots: values.slots.sort((slot) => slot.order)
    };

    if (isEditPage) {
      updateCeremonyTemplate(payloadCeremonyTemplate);
    } else {
      createCeremonyTemplate(payloadCeremonyTemplate);
    }
  };

  const { mutate: updateCeremonyTemplate } = useUpdateCeremonyTemplateMutation(queryClient, {
    onSuccess: () => {
      navigate(`/ceremony-templates/${id}`);
      showToast(ToastType.SUCCESS, t('ceremonyTemplateSuccessfullyCreated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const { mutate: createCeremonyTemplate } = useCreateCeremonyTemplateMutation(queryClient, {
    onSuccess: () => {
      navigate('/ceremony-templates');
      showToast(ToastType.SUCCESS, t('ceremonyTemplateSuccessfullyCreated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const {
    data: allOrganizations,
    fetchNextPage: fetchNextPageOrganizations,
    hasNextPage: hasNextPageOrganizations
  } = useGetAllOrganizations(
    PAGE_SIZE,
    searchOrganization,
    undefined,
    hasPermissionViewOrganizations
  );

  const handleDeleteSlot = (
    slotToDelete: CeremonyTemplateSlot,
    setFieldValue?: (field: string, value: CeremonyTemplateSlot[]) => void
  ) => {
    if (!ceremonySlots) {
      return;
    }

    const updatedSlots = ceremonySlots.filter((slot) => slot.id !== slotToDelete.id);

    const reorderedSlots = updatedSlots.map((slot, index) => ({
      ...slot,
      order: index + 1
    }));

    setCeremonySlots(reorderedSlots);

    if (setFieldValue) {
      setFieldValue('slots', reorderedSlots);
    }
  };

  const handleSlotClick = () => {
    return;
  };

  const handleUpdateAssetOrder = async (
    currentOrder: number,
    targetedOrder: number,
    setFieldValue?: (field: string, value: CeremonyTemplateSlot[]) => void
  ) => {
    if (!ceremonySlots || currentOrder === targetedOrder) {
      return;
    }

    const updatedSlots = [...ceremonySlots];

    const draggedSlotIndex = updatedSlots.findIndex((slot) => slot.order === currentOrder);
    const targetSlotIndex = updatedSlots.findIndex((slot) => slot.order === targetedOrder);

    if (draggedSlotIndex === -1 || targetSlotIndex === -1) {
      return;
    }

    const [draggedSlot] = updatedSlots.splice(draggedSlotIndex, 1);

    updatedSlots.splice(targetSlotIndex, 0, draggedSlot);

    const reorderedSlots = updatedSlots.map((slot, index) => ({
      ...slot,
      order: index + 1
    }));

    setCeremonySlots(reorderedSlots);

    if (setFieldValue) {
      setFieldValue('slots', reorderedSlots);
    }
  };

  const dndBackends = {
    backends: [
      { backend: HTML5Backend },
      {
        backend: TouchBackend,
        options: { enableMouseEvents: true },
        preview: true,
        transition: TouchTransition
      }
    ]
  };

  const getOrganizationOptions = () => {
    return (
      allOrganizations?.pages.flatMap((page) =>
        page.data?.items.map((organization: Organization) => ({
          value: organization.id,
          label: organization.name
        }))
      ) || []
    );
  };

  return (
    <div className='flex h-screen justify-center overflow-hidden'>
      {isEditPage && isLoadingCeremonyTemplate ? (
        <Spinner />
      ) : error ? (
        <ErrorPage code={error} />
      ) : (
        <DndProvider backend={MultiBackend} options={dndBackends}>
          <div className='flex size-full min-h-0 justify-center'>
            <div className='mx-3 mt-7 flex min-h-0 w-full flex-col md:mx-20'>
              <div className='w-full'>
                <Breadcrumb
                  icon={
                    <CeremonyTemplateIcon className='h-4 stroke-[0.5] text-xl text-primary-blue' />
                  }
                  iconPath={'/ceremony-templates'}
                  path={[{ label: label }]}
                />
              </div>
              <div className='mt-9 flex min-h-0 flex-col'>
                <Label text={label} type={LABEL_TYPE.H1} />
                <Label text={t('generalInformation')} type={LABEL_TYPE.H3_BOLD} className='my-9' />
                <Formik
                  enableReinitialize
                  validationSchema={
                    isEditPage ? updateCeremonyTemplateScheme : createCeremonyTemplateScheme
                  }
                  initialValues={getInitialValues()}
                  onSubmit={handleSubmit}>
                  {({
                    touched,
                    values,
                    setFieldValue,
                    setFieldTouched,
                    errors,
                    isValid,
                    dirty
                  }) => {
                    return (
                      <Form id='CreateEdit' className='flex min-h-0 flex-col'>
                        <div className='flex min-h-0 flex-col gap-y-6'>
                          <div className='grid min-h-0 grid-cols-2 gap-x-9'>
                            <div className='flex flex-col gap-y-3'>
                              <Input
                                label={t('templateName')}
                                type='text'
                                id='name'
                                isRequired
                                errorMessage={touched.name ? errors.name : undefined}
                                isInvalid={!!errors.name && touched.name}
                              />
                              <div className={clsx(isEditPage && 'cursor-not-allowed opacity-60')}>
                                <SingleSelect
                                  id='organizationId'
                                  options={getOrganizationOptions()}
                                  handleChange={(option: SelectOptionProps | null) => {
                                    if (!isEditPage) {
                                      setFieldValue('organizationId', option?.value ?? 0);
                                      setSelectedOrganizationId(Number(option?.value ?? 0));
                                    }
                                  }}
                                  value={
                                    getOrganizationOptions().find(
                                      (org) => org.value === values.organizationId
                                    ) || null
                                  }
                                  isSearchable={!isEditPage}
                                  isClearable={!isEditPage}
                                  disabled={isEditPage}
                                  isRequired={true}
                                  label={t('organization')}
                                  placeholder={
                                    isEditPage
                                      ? ceremonyTemplate?.data.organization?.name
                                      : t('organization')
                                  }
                                  handleInputChange={isEditPage ? undefined : setSearchOrganization}
                                  fetchNextPage={fetchNextPageOrganizations}
                                  hasNextPage={hasNextPageOrganizations}
                                  className='w-full'
                                  onBlur={() => setFieldTouched('organizationId', true)}
                                  errorMessage={
                                    ceremonySetupMissingMessage ?? (errors.organizationId as string)
                                  }
                                  isInvalid={
                                    !!ceremonySetupMissingMessage ||
                                    (!!errors.organizationId && !!touched.organizationId)
                                  }
                                />
                              </div>
                            </div>

                            <div
                              className={`no-scrollbar flex min-h-0 w-full flex-wrap overflow-y-auto ${isReorderingEnabled ? 'rounded-2xl border-2 border-accent-light-blue md:border-0' : ''} ${isReorderingEnabled ? 'pt-1' : 'pt-5'} max-h-[calc(100vh-260px)] pr-2`}>
                              <div className='flex w-full flex-wrap'>
                                {ceremonySlots?.map((slot) => (
                                  <EventTemplateSlotCard
                                    type={slot.slotType as CeremonySlotType}
                                    key={slot.id}
                                    slot={slot as CeremonyTemplateSlot}
                                    deleteCeremonyTemplateSlot={(slotToDelete) => {
                                      handleDeleteSlot(slotToDelete, setFieldValue);
                                    }}
                                    handleUpdateSlotOrder={(currentOrder, targetedOrder) => {
                                      handleUpdateAssetOrder(
                                        currentOrder,
                                        targetedOrder,
                                        setFieldValue
                                      );
                                    }}
                                    onClick={() => handleSlotClick()}
                                    isReorderingEnabled={isReorderingEnabled}
                                    setIsReorderingEnabled={setIsReorderingEnabled}
                                  />
                                ))}
                                <div className='w-1/2 p-2 md:w-1/4'>
                                  <AddEventTemplateSlot
                                    organizationId={values.organizationId}
                                    hasCeremonySetupAccess={hasCeremonySetupAccess}
                                    slots={ceremonySlots || []}
                                    onSlotsChange={(newSlots) => {
                                      setCeremonySlots(newSlots);
                                      setFieldValue('slots', newSlots);
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='my-5 flex w-full justify-end'>
                          <Button
                            variant={ButtonVariant.PRIMARY}
                            type='submit'
                            disabled={
                              isSubmitting || !(isValid && dirty) || !hasCeremonySetupAccess
                            }>
                            {isEditPage ? t('saveChanges') : t('addCeremonyTemplate')}
                          </Button>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </div>
            </div>
          </div>
        </DndProvider>
      )}
    </div>
  );
};
